import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiChevronLeft, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: "United Kingdom",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  postalCode: "",
  city: "",
  cardNumber: "",
  expiry: "",
  security: "",
  cardName: "",
};

const CheckOut = () => {
  const navigate = useNavigate();
  const { cartItems, clearCart } = useContext(CartContext);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("relay");

  useEffect(() => {
    document.title = `Secure Checkout — ${siteConfig.brandName}`;
  }, []);

  const items = cartItems
    .map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) }))
    .filter((item) => item.product);
  const subtotal = items.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const shipping = subtotal >= 200 || shippingMethod === "relay" ? 0 : 8;
  const total = subtotal + shipping;

  const updateField = (event) => {
    const { name, value } = event.target;
    let nextValue = value;
    if (name === "cardNumber") nextValue = value.replace(/\D/g, "").slice(0, 16).replace(/(.{4})/g, "$1 ").trim();
    if (name === "expiry") nextValue = value.replace(/\D/g, "").slice(0, 4).replace(/^(\d{2})(\d)/, "$1 / $2");
    if (name === "security") nextValue = value.replace(/\D/g, "").slice(0, 4);
    setForm((current) => ({ ...current, [name]: nextValue }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) nextErrors.email = "Enter a valid email address.";
    ["firstName", "lastName", "address", "postalCode", "city", "cardName"].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required.";
    });
    if (form.cardNumber.replace(/\s/g, "").length !== 16) nextErrors.cardNumber = "Enter the 16-digit card number.";
    if (form.expiry.replace(/\D/g, "").length !== 4) nextErrors.expiry = "Invalid expiry date.";
    if (form.security.length < 3) nextErrors.security = "Invalid security code.";
    return nextErrors;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const nextErrors = validate();
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      document.querySelector(".checkout-form [aria-invalid='true']")?.focus();
      return;
    }
    setSubmitting(true);
    const orderNumber = `TV-${String(Date.now()).slice(-6)}`;
    window.setTimeout(() => {
      clearCart();
      navigate("/paysuccess", { state: { orderNumber, email: form.email, total } });
    }, 700);
  };

  if (!items.length) {
    return (
      <main className="checkout-empty shell page-shell">
        <p className="eyebrow">Checkout</p>
        <h1>Your cart is empty.</h1>
        <Link className="btn" to="/shop">Return to shop</Link>
      </main>
    );
  }

  const field = (name, label, props = {}) => (
    <label className={`checkout-field ${errors[name] ? "has-error" : ""}`}>
      <span>{label}</span>
      <input
        name={name}
        value={form[name]}
        onChange={updateField}
        aria-invalid={Boolean(errors[name])}
        {...props}
      />
      {errors[name] && <small>{errors[name]}</small>}
    </label>
  );

  return (
    <main className="checkout-page">
      <div className="checkout-layout shell">
        <form className="checkout-form" onSubmit={handleSubmit} noValidate>
          <Link className="checkout-back" to="/cartpage"><FiChevronLeft /> Back to cart</Link>
          <div className="checkout-wordmark">{siteConfig.displayName}</div>

          <section>
            <header><span>01</span><h2>Contact</h2></header>
            {field("email", "Email", { type: "email", autoComplete: "email", placeholder: "you@example.com" })}
            <label className="checkout-checkbox"><input type="checkbox" defaultChecked /> <span>Receive order updates by email</span></label>
          </section>

          <section>
            <header><span>02</span><h2>Delivery</h2></header>
            <label className="checkout-field">
              <span>Country / region</span>
              <select name="country" value={form.country} onChange={updateField}>
                <option>United Kingdom</option><option>France</option><option>Belgium</option><option>Ireland</option><option>Germany</option><option>Spain</option><option>Italy</option>
              </select>
            </label>
            <div className="checkout-row">{field("firstName", "First name", { autoComplete: "given-name" })}{field("lastName", "Last name", { autoComplete: "family-name" })}</div>
            {field("company", "Company (optional)", { autoComplete: "organization" })}
            {field("address", "Address", { autoComplete: "street-address" })}
            {field("apartment", "Apartment, suite, etc. (optional)")}
            <div className="checkout-row checkout-row--city">{field("postalCode", "Postcode", { autoComplete: "postal-code" })}{field("city", "City", { autoComplete: "address-level2" })}</div>

            <div className="shipping-methods">
              <label><input type="radio" name="shipping" checked={shippingMethod === "relay"} onChange={() => setShippingMethod("relay")} /><span><b>Collection point</b><small>2 to 4 business days</small></span><strong>Free</strong></label>
              <label><input type="radio" name="shipping" checked={shippingMethod === "home"} onChange={() => setShippingMethod("home")} /><span><b>Home delivery</b><small>Timing depends on destination</small></span><strong>{subtotal >= 200 ? "Free" : formatPrice(8)}</strong></label>
            </div>
          </section>

          <section>
            <header><span>03</span><h2>Payment</h2></header>
            <p className="checkout-section-note"><FiLock /> All transactions are secure and encrypted.</p>
            <div className="payment-card">
              <div className="payment-card__heading"><span>Credit or debit card</span><span>VISA · MC</span></div>
              <div className="payment-card__body">
                {field("cardNumber", "Card number", { inputMode: "numeric", autoComplete: "cc-number", placeholder: "1234 5678 9012 3456" })}
                <div className="checkout-row">{field("expiry", "Expiry date", { inputMode: "numeric", autoComplete: "cc-exp", placeholder: "MM / YY" })}{field("security", "Security code", { inputMode: "numeric", autoComplete: "cc-csc", placeholder: "CVC" })}</div>
                {field("cardName", "Name on card", { autoComplete: "cc-name" })}
              </div>
            </div>
          </section>

          <button className="btn checkout-submit" type="submit" disabled={submitting}>
            {submitting ? "Processing..." : `Pay ${formatPrice(total)}`}
          </button>
          <p className="checkout-legal">By placing your order, you agree to our <Link to="/terms">terms and conditions</Link> and <Link to="/privacy">privacy policy</Link>.</p>
        </form>

        <aside className="checkout-summary">
          <h2>Your Order</h2>
          <div className="checkout-products">
            {items.map((item) => (
              <article key={`${item.id}-${item.size}-${item.color}`}>
                <div><img src={item.product.images[0]} alt={item.product.name} /><span>{item.quantity}</span></div>
                <p><b>{item.product.name}</b><small>{item.color} · {item.size}</small></p>
                <strong>{formatPrice(item.product.price * item.quantity)}</strong>
              </article>
            ))}
          </div>
          <dl>
            <div><dt>Subtotal</dt><dd>{formatPrice(subtotal)}</dd></div>
            <div><dt>Delivery</dt><dd>{shipping === 0 ? "Free" : formatPrice(shipping)}</dd></div>
            <div className="checkout-total"><dt>Total <small>GBP</small></dt><dd>{formatPrice(total)}</dd></div>
          </dl>
        </aside>
      </div>
    </main>
  );
};

export default CheckOut;
